import { useCallback, useEffect, useMemo, useState } from "react";
import { ProductData } from "../types";

// Helper function to parse Italian formatted numbers (e.g., "1.234,56" -> 1234.56)
function parseItalianNumber(value: string): number {
  if (value === "-" || value === "" || value === null || value === undefined) {
    return -Infinity; // Sort "-" values to the end
  }
  // Remove thousands separator (.) and replace decimal separator (,) with (.)
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? -Infinity : parsed;
}

interface UseStrumentiImportedSortProps {
  data: ProductData[];
  sortableColumns: string[];
}

function useStrumentiImportedSort({ data, sortableColumns }: UseStrumentiImportedSortProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string;
  }>({
    key: null,
    direction: "asc",
  });

  // Sorting function with proper Italian number handling
  const sortedData = useMemo(() => {
    // If no sort config is set, return the original data
    if (!sortConfig.key) {
      return data;
    }

    const key = sortConfig.key;
    const direction = sortConfig.direction;

    return [...data].sort((a, b) => {
      // Apply different sorting logic based on the key
      switch (key) {
        case "peso_sul_ptf":
        case "controvalore":
        case "costi_totali_stimati":
        case "rating_ESG":
        case "final_score":
        case "final_score_advice":
        case "ytm":
        case "duration":
        case "pl_fiscale_euro":
        case "pl_fiscale_perc":
        case "rateo_euro":
        case "commissioni_gestione":
        case "abbuoni":
        case "dividend_yield":
        case "giacenza_media":
          // Numeric sorting with Italian number parsing
          const numA = parseItalianNumber(a[key]);
          const numB = parseItalianNumber(b[key]);
          return direction === "asc" ? numA - numB : numB - numA;

        case "scadenza":
          // Date sorting (assuming format is DD/MM/YYYY)
          const dateA = a[key] ? new Date(String(a[key]).split('/').reverse().join('-')).getTime() : 0;
          const dateB = b[key] ? new Date(String(b[key]).split('/').reverse().join('-')).getTime() : 0;
          return direction === "asc" ? dateA - dateB : dateB - dateA;

        case "microcategoria_advice":
        case "tipo_emittente":
        case "tipo_bond":
        case "paese":
          // Alphabetical sorting
          const strA = String(a[key] || "").toLowerCase();
          const strB = String(b[key] || "").toLowerCase();
          return direction === "asc"
            ? strA.localeCompare(strB)
            : strB.localeCompare(strA);

        default:
          // Default string sorting
          const valA = String(a[key] || "").toLowerCase();
          const valB = String(b[key] || "").toLowerCase();
          return direction === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
      }
    });
  }, [data, sortConfig]);

  const handleSort = useCallback(
    (key: string) => {
      // Only allow sorting on sortable columns
      if (!sortableColumns.includes(key)) {
        return;
      }

      // Toggle between asc and desc
      setSortConfig((prevConfig) => {
        let direction = "asc";
        if (prevConfig.key === key && prevConfig.direction === "asc") {
          direction = "desc";
        }
        return { key, direction };
      });
    },
    [sortableColumns],
  );

  // Set default sort on mount
  useEffect(() => {
    handleSort("microcategoria_advice");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sortConfig,
    sortedData,
    handleSort,
  };
}

export default useStrumentiImportedSort;
